import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Card,
  Button,
  DatePicker,
  Input,
  Spin,
  message,
  Form,
  Grid,
  Empty,
} from 'antd'
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import PurchaseList from '../components/Purchase/PurchaseList'
import purchaseService from '../services/purchaseService'
import './PurchaseHistory.css'
import '../styles/mobile-record.css'

const { RangePicker } = DatePicker

const defaultFilters = {
  dateRange: null,
  ticker: '',
}

const PurchaseHistory = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [filters, setFilters] = useState(defaultFilters)
  const filtersRef = useRef(filters)
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [filtersOpen, setFiltersOpen] = useState(!isMobile)
  const hasRecords = Array.isArray(data) && data.length > 0

  useEffect(() => {
    filtersRef.current = filters
    form.setFieldsValue({
      dateRange: filters.dateRange || null,
      ticker: filters.ticker || '',
    })
  }, [filters, form])

  useEffect(() => {
    setFiltersOpen(!isMobile)
  }, [isMobile])

  const buildQueryParams = useCallback((filterState, override = {}) => {
    const currentPage = override.current ?? pagination.current
    const pageSize = override.pageSize ?? pagination.pageSize

    return {
      page: override.page ?? (currentPage - 1),
      pageSize,
      startDate: filterState.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: filterState.dateRange?.[1]?.format('YYYY-MM-DD'),
      ticker: filterState.ticker?.trim() || undefined,
    }
  }, [pagination.current, pagination.pageSize])

  const loadPurchaseHistory = useCallback(async (override = {}) => {
    setLoading(true)
    try {
      const activeFilters = override.filters ?? filtersRef.current
      const queryParams = buildQueryParams(activeFilters, override)

      const response = await purchaseService.getPurchaseHistory(queryParams)

      setData(response.content || [])
      setPagination((prev) => ({
        ...prev,
        current: (response.currentPage || 0) + 1,
        pageSize: response.pageSize || queryParams.pageSize || prev.pageSize,
        total: response.totalElements || 0,
      }))
    } catch (error) {
      console.error('Error loading purchase history:', error)
      message.error('Failed to load purchase history. Please try again.')
      setData([])
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams])

  useEffect(() => {
    loadPurchaseHistory({ page: 0, filters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTableChange = (paginationConfig) => {
    const updatedPagination = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }
    setPagination((prev) => ({
      ...prev,
      ...updatedPagination,
    }))

    loadPurchaseHistory({
      current: updatedPagination.current,
      page: updatedPagination.current - 1,
      pageSize: updatedPagination.pageSize,
      filters,
    })
  }

  const handleApplyFilters = useCallback((values) => {
    const normalizedFilters = {
      dateRange: values.dateRange || null,
      ticker: values.ticker?.trim() || '',
    }
    setFilters(normalizedFilters)
    setPagination((prev) => ({ ...prev, current: 1 }))
    loadPurchaseHistory({
      page: 0,
      pageSize: pagination.pageSize,
      filters: normalizedFilters,
    })
  }, [loadPurchaseHistory, pagination.pageSize])

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
    form.resetFields()
    setPagination((prev) => ({ ...prev, current: 1 }))
    loadPurchaseHistory({
      page: 0,
      pageSize: pagination.pageSize,
      filters: defaultFilters,
    })
  }, [form, loadPurchaseHistory, pagination.pageSize])

  return (
    <div className={`purchase-history-page ${isMobile ? 'mobile-record-page' : ''}`}>
      {isMobile && (
        <div className="purchase-history-toolbar mobile-record-toolbar">
          <Button
            icon={<FilterOutlined />}
            type="primary"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="purchase-history-toggle-btn mobile-record-toggle-btn"
          >
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>
      )}

      {(!isMobile || filtersOpen) && (
        <Card
          bordered={false}
          className="purchase-history-filter-card mobile-record-filter-card"
          styles={{ body: { padding: isMobile ? 16 : 24 } }}
        >
          <Form
            form={form}
            layout="vertical"
            className="purchase-history-filter-form mobile-record-filter-form"
            onFinish={handleApplyFilters}
          >
            <div className="purchase-history-filter-grid mobile-record-filter-grid">
              <Form.Item name="ticker" label={null} colon={false}>
                <Input
                  size="large"
                  placeholder="Search ticker..."
                  allowClear
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                />
              </Form.Item>

              <Form.Item name="dateRange" label={null} colon={false}>
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  placeholder={['Start', 'End']}
                />
              </Form.Item>

              <div className="purchase-history-filter-actions mobile-record-filter-actions">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  className="purchase-history-search-btn mobile-record-search-btn"
                >
                  Search
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilters}
                  size="large"
                  className="purchase-history-reset-btn mobile-record-reset-btn"
                >
                  Reset
                </Button>
              </div>
            </div>
          </Form>
        </Card>
      )}

      {isMobile ? (
        hasRecords ? (
          <Spin spinning={loading}>
            <PurchaseList
              data={data}
              loading={loading}
              onChange={handleTableChange}
              pagination={pagination}
              isMobile={isMobile}
            />
          </Spin>
        ) : (
          <Card className="purchase-history-empty-card">
            {loading ? (
              <div className="purchase-history-empty-loading">
                <Spin size="large" />
              </div>
            ) : (
              <Empty description="No transaction records found" />
            )}
          </Card>
        )
      ) : (
        <Card
          bordered={false}
          className="purchase-history-table-card"
          styles={{ body: { padding: 0 } }}
        >
          <PurchaseList
            data={data}
            loading={loading}
            onChange={handleTableChange}
            pagination={pagination}
            isMobile={isMobile}
          />
        </Card>
      )}

    </div>
  )
}

export default PurchaseHistory
